import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "../lib/trpc.js";
import { Button } from "../components/ui/Button.js";
import { NavActions } from "../components/NavActions.js";

function formatDate(d: Date | string) {
  return new Date(d).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

type Candidate = {
  clubBookId: string;
  book: { title: string; authors: string[]; coverUrl: string | null } | null;
  points: number;
};

function RankBadge({ rank }: { rank: 1 | 2 | 3 }) {
  const { t } = useTranslation();
  const labels = {
    1: t("polls.view.ballot1st"),
    2: t("polls.view.ballot2nd"),
    3: t("polls.view.ballot3rd"),
  };
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
      style={{ background: "var(--color-accent-subtle)", color: "var(--color-accent-subtle-foreground)" }}
    >
      {labels[rank]}
    </span>
  );
}

export function PollView() {
  const { clubId, pollId } = useParams<{ clubId: string; pollId: string }>();
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const { data: poll, isLoading } = trpc.polls.get.useQuery(
    { pollId: pollId! },
    { enabled: !!pollId }
  );

  const vote = trpc.polls.vote.useMutation({
    onSuccess: () => utils.polls.get.invalidate({ pollId }),
  });

  const [first, setFirst] = useState<string | undefined>();
  const [second, setSecond] = useState<string | undefined>();
  const [third, setThird] = useState<string | undefined>();

  function toggleRank(id: string, rank: "first" | "second" | "third") {
    const setters = { first: setFirst, second: setSecond, third: setThird };
    const current = { first, second, third };

    if (current.first === id && rank !== "first") setFirst(undefined);
    if (current.second === id && rank !== "second") setSecond(undefined);
    if (current.third === id && rank !== "third") setThird(undefined);

    const currentVal = current[rank];
    setters[rank](currentVal === id ? undefined : id);
  }

  function getRankFor(id: string): 1 | 2 | 3 | null {
    if (first === id) return 1;
    if (second === id) return 2;
    if (third === id) return 3;
    return null;
  }

  function handleSubmit() {
    if (!first) return;
    vote.mutate({ pollId: pollId!, first, second, third });
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: "var(--color-background)" }}>
        <span className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>{t("common.loading")}</span>
      </div>
    );
  }

  if (!poll) return null;

  const isOpen = poll.state === "OPEN";
  const maxPoints = Math.max(...poll.candidates.map((c) => c.points), 1);

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      <header
        className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 md:px-10"
        style={{
          background: "oklch(97% 0.014 82 / 0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <Link
          to={`/clubs/${clubId}`}
          className="text-sm flex-shrink-0"
          style={{ color: "var(--color-muted-foreground)", textDecoration: "none" }}
        >
          {t("nav.backToClub")}
        </Link>
        <span style={{ color: "var(--color-border-strong)" }}>|</span>
        <span className="font-serif font-semibold truncate" style={{ color: "var(--color-foreground)" }}>
          {poll.parentPollId ? t("polls.view.runoffTitle") : t("polls.view.pollTitle")}
        </span>
        <span
          className="rounded-full px-2 py-0.5 text-xs flex-shrink-0"
          style={{
            background: isOpen ? "var(--color-accent-subtle)" : "var(--color-secondary)",
            color: isOpen ? "var(--color-accent-subtle-foreground)" : "var(--color-muted-foreground)",
          }}
        >
          {poll.state === "OPEN"
            ? t("polls.view.statusOpen")
            : poll.state === "UNRESOLVED"
            ? t("polls.view.statusUnresolved")
            : t("polls.view.statusClosed")}
        </span>
        <div className="ml-auto"><NavActions /></div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10 space-y-8 md:px-10">
        {/* Meta */}
        <div style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) both" }}>
          <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
            {isOpen
              ? t("polls.view.membersVoted", { voted: poll.totalVotes, total: poll.eligibleCount }) + ` · ${t("polls.view.deadline")} ${formatDate(poll.deadline)}`
              : `${t("polls.view.closedOn")} ${poll.closedAt ? formatDate(poll.closedAt) : ""} · ${poll.totalVotes} votes`}
          </p>
          {isOpen && (
            <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-secondary)" }}>
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${poll.eligibleCount > 0 ? (poll.totalVotes / poll.eligibleCount) * 100 : 0}%`,
                  background: "var(--color-accent)",
                }}
              />
            </div>
          )}
        </div>

        {/* Already voted / not eligible */}
        {poll.hasVoted && (
          <div
            className="rounded-xl px-4 py-3 text-sm"
            style={{ background: "var(--color-accent-subtle)", color: "var(--color-accent-subtle-foreground)" }}
          >
            You voted · {poll.myBallot?.first ? "1st, " : ""}{poll.myBallot?.second ? "2nd, " : ""}{poll.myBallot?.third ? "3rd" : ""}
            {" "}selections locked in.
          </div>
        )}
        {isOpen && !poll.isEligible && !poll.hasVoted && (
          <div
            className="rounded-xl px-4 py-3 text-sm"
            style={{ background: "var(--color-secondary)", color: "var(--color-muted-foreground)" }}
          >
            {t("polls.view.notEligible")}
          </div>
        )}

        {/* Candidates */}
        <div className="space-y-3" style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}>
          <p className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-accent)" }}>
            {isOpen && !poll.hasVoted && poll.isEligible ? t("polls.view.rankYourChoices") : t("polls.view.standings")}
          </p>

          {poll.candidates.map((c: Candidate, i: number) => {
            const rank = getRankFor(c.clubBookId);
            const isWinner = !isOpen && i === 0 && poll.state === "CLOSED";
            const pct = maxPoints > 0 ? (c.points / maxPoints) * 100 : 0;
            const canVote = isOpen && !poll.hasVoted && poll.isEligible;

            return (
              <div
                key={c.clubBookId}
                className="rounded-xl p-4 transition-all"
                style={{
                  background: isWinner ? "var(--color-accent-subtle)" : "var(--color-card)",
                  border: `1px solid ${rank ? "var(--color-accent)" : isWinner ? "var(--color-accent)" : "var(--color-border)"}`,
                  boxShadow: "var(--shadow-xs)",
                  cursor: canVote ? "pointer" : "default",
                }}
                onClick={() => {
                  if (!canVote) return;
                  if (!rank) toggleRank(c.clubBookId, "first");
                  else if (rank === 1) { setFirst(undefined); toggleRank(c.clubBookId, "second"); }
                  else if (rank === 2) { setSecond(undefined); toggleRank(c.clubBookId, "third"); }
                  else { setThird(undefined); }
                }}
              >
                <div className="flex items-center gap-3">
                  {c.book?.coverUrl ? (
                    <img
                      src={c.book.coverUrl}
                      alt=""
                      className="w-8 h-11 object-cover rounded flex-shrink-0"
                      style={{ background: "var(--color-muted)" }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-8 h-11 rounded flex-shrink-0" style={{ background: "var(--color-muted)" }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-serif text-sm font-medium truncate" style={{ color: "var(--color-foreground)" }}>
                        {c.book?.title ?? "Unknown"}
                      </p>
                      {isWinner && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "var(--color-accent)", color: "white" }}>
                          {t("polls.view.winner")}
                        </span>
                      )}
                      {rank && canVote && <RankBadge rank={rank} />}
                    </div>
                    <p className="text-xs truncate mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>
                      {c.book?.authors?.join(", ")}
                    </p>
                    <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: "var(--color-secondary)" }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: isWinner ? "var(--color-accent)" : "var(--color-foreground)", opacity: isWinner ? 1 : 0.3 }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: "var(--color-muted-foreground)" }}>
                      {c.points} {c.points === 1 ? t("polls.view.ptSingular") : t("polls.view.ptPlural")}
                    </p>
                  </div>
                  {canVote && (
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {([["first", 1], ["second", 2], ["third", 3]] as const).map(([slot, n]) => (
                        <button
                          key={slot}
                          className="text-xs w-14 rounded px-1.5 py-0.5 text-center transition-all"
                          style={{
                            border: `1px solid ${{ first, second, third }[slot] === c.clubBookId ? "var(--color-accent)" : "var(--color-border)"}`,
                            background: { first, second, third }[slot] === c.clubBookId ? "var(--color-accent-subtle)" : "var(--color-background)",
                            color: { first, second, third }[slot] === c.clubBookId ? "var(--color-accent-subtle-foreground)" : "var(--color-muted-foreground)",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRank(c.clubBookId, slot);
                          }}
                        >
                          {n === 1 ? t("polls.view.rank1st") : n === 2 ? t("polls.view.rank2nd") : t("polls.view.rank3rd")}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Submit ballot */}
        {isOpen && !poll.hasVoted && poll.isEligible && (
          <div className="space-y-2" style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both" }}>
            <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
              {t("polls.view.selectFirst")}. {t("polls.view.ballotFinal")}.
            </p>
            {vote.error && (
              <p className="text-xs" style={{ color: "var(--color-destructive)" }}>{vote.error.message}</p>
            )}
            <Button
              disabled={!first}
              loading={vote.isPending}
              onClick={handleSubmit}
            >
              {t("polls.view.submitBallot")}
            </Button>
          </div>
        )}

        {poll.state === "UNRESOLVED" && (
          <div
            className="rounded-xl p-4"
            style={{ background: "var(--color-card)", border: "1px solid var(--color-destructive)", opacity: 0.9 }}
          >
            <p className="text-sm font-medium" style={{ color: "var(--color-destructive)" }}>{t("polls.view.unresolved")}</p>
            <p className="text-xs mt-1" style={{ color: "var(--color-muted-foreground)" }}>
              {t("polls.view.runoffTie")}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
